import { useEffect } from 'react';

export interface Shortcuts {
  onSave?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onPalette?: () => void;
}

function inTextEdit(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * The global shortcuts (spec section 2.2): Ctrl/Cmd+K the palette, Ctrl/Cmd+S Save All, Ctrl/Cmd+Z undo,
 * Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z redo. Undo / redo are inert while a text field is being edited (it keeps the browser's
 * own undo until the edit commits); Save and the palette work everywhere.
 */
export function KeyboardShortcuts({ onSave, onUndo, onRedo, onPalette }: Shortcuts) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const key = e.key.toLowerCase();
      if (key === 'k') {
        e.preventDefault();
        onPalette?.();
      } else if (key === 's') {
        e.preventDefault();
        onSave?.();
      } else if (!inTextEdit(e.target)) {
        if (key === 'z' && !e.shiftKey) {
          e.preventDefault();
          onUndo?.();
        } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
          e.preventDefault();
          onRedo?.();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSave, onUndo, onRedo, onPalette]);
  return null;
}
